import { Link } from "@chakra-ui/react";
import { InputFieldMetadata } from "@duet/shared/pvComponents/types";
import { keys } from "lodash";
import { z } from "zod";
import { CellTemperatureModel, SystemParameters } from "~/gql/generated";

type SystemParametersData = Omit<
  SystemParameters,
  | "id"
  | "created_at"
  | "updated_at"
  | "project_id"
  | "pv_hv_transformer"
  | "pv_hv_ac_wire"
  | "terrain_dem_enabled"
  | "__typename"
>;

export type SystemParameterField = keyof SystemParametersData;

export const systemParamsMetadata: Record<
  keyof SystemParametersData,
  InputFieldMetadata<SystemParametersData>
> = {
  hv_stage_enabled: {
    labelText: "HV Stage Enabled",
  },
  pv_hv_ac_wire_id: {
    labelText: "AC Wire",
    hideIf: ({ hv_stage_enabled }) => !hv_stage_enabled,
  },
  pv_hv_transformer_id: {
    labelText: "HV Transformer",
    hideIf: ({ hv_stage_enabled }) => !hv_stage_enabled,
  },
  grid_injection_limit_enabled: {
    labelText: "Grid Injection Limit Enabled",
  },
  grid_injection_limit: {
    labelText: "Grid Injection Limit",
    unit: "W",
    displayUnit: "MW",
    hideIf: ({ grid_injection_limit_enabled }) => !grid_injection_limit_enabled,
  },
  cell_temperature_model: {
    labelText: "Cell Temperature Model",
    options: [
      {
        label: "Sandia",
        value: "sandia",
        helpText: (
          <>
            D.L. King, J.A. Kratochvil, W.E. Boyson, ‘Photovoltaic array
            performance model,’ Sandia Report, SAND2004-3535, pp.1–43, 2004; (
            <Link
              href={"https://www.osti.gov/servlets/purl/919131"}
              target="_blank"
            >
              https://www.osti.gov/servlets/purl/919131
            </Link>
            )
          </>
        ),
      },
      {
        label: "Faiman",
        value: "faiman",
        helpText: (
          <>
            D. Faiman, ‘Assessing the outdoor operating temperature of
            photovoltaic modules,’ Progress in Photovoltaics, 16(4), 307-315
            (2008); (
            <Link href={"https://doi.org/10.1002/pip.813"} target="_blank">
              https://doi.org/10.1002/pip.813
            </Link>
            )
          </>
        ),
      },
      {
        label: "PVsyst",
        value: "pvsyst",
        helpText: (
          <>
            PVsyst Array Thermal Losses Model:{" "}
            <Link
              href={
                "https://www.pvsyst.com/help/project-design/array-and-system-losses/array-thermal-losses/index.html"
              }
              target="_blank"
            >
              https://www.pvsyst.com/help/project-design/array-and-system-losses/array-thermal-losses/index.html
            </Link>
          </>
        ),
      },
      {
        label: "NOCT",
        value: "noct",
        helpText: "Nominal Operating Cell Temperature",
      },
      {
        label: "Ambient",
        value: "ambient",
        helpText:
          "Set all cell temperatures to the ambient temperature for each timestamp.",
      },
      {
        label: "STC",
        value: "stc",
        helpText:
          "Set all cell temperatures to the STC temperature (25°C) for each timestamp.",
      },
    ],
  },
  sandia_panel_type: {
    labelText: "Sandia Panel Type",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "sandia",
  },
  sandia_a: {
    labelText: "Sandia A",
    helpText:
      "Empirical coefficient for temperature under low wind speeds and high solar irradiance [unitless]",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "sandia",
    disabledIf: ({ sandia_panel_type }) => Boolean(sandia_panel_type),
  },
  sandia_b: {
    labelText: "Sandia B",
    helpText:
      "Empirical coefficient for temperature rate of change with windspeed [1/(m/s)]",
    unit: "1/(m/s)",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "sandia",
    disabledIf: ({ sandia_panel_type }) => Boolean(sandia_panel_type),
  },
  sandia_delta_t: {
    labelText: "Sandia ΔT",
    helpText:
      "Temperature difference between the cell and module back surface under 1000 W/m² irradiance",
    unit: "°C",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "sandia",
    disabledIf: ({ sandia_panel_type }) => Boolean(sandia_panel_type),
  },
  sandia_e0: {
    labelText: "Sandia E0",
    helpText: "Reference solar irradiance, typically 1000 W/m²",
    unit: "W/m²",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "sandia",
    disabledIf: ({ sandia_panel_type }) => Boolean(sandia_panel_type),
  },
  faiman_u0: {
    labelText: "Faiman U0",
    helpText: "Heat loss coefficient - constant [W/m²/°C]",
    unit: "W/m²/°C",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "faiman",
  },
  faiman_u1: {
    labelText: "Faiman U1",
    helpText: "Heat loss coefficient - wind dependent [W/m²/°C/(m/s)]",
    unit: "W/m²/°C/(m/s)",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "faiman",
  },
  pvsyst_mounting_type: {
    labelText: "PVsyst Mounting Type",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "pvsyst",
  },
  pvsyst_uc: {
    labelText: "PVsyst Uc",
    helpText: "Heat loss factor - constant",
    unit: "W/m²/°C",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "pvsyst",
    disabledIf: ({ pvsyst_mounting_type }) => Boolean(pvsyst_mounting_type),
  },
  pvsyst_uv: {
    labelText: "PVsyst Uv",
    helpText: "Heat loss factor - wind dependent",
    unit: "W/m²/°C/(m/s)",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "pvsyst",
    disabledIf: ({ pvsyst_mounting_type }) => Boolean(pvsyst_mounting_type),
  },
  pvsyst_alpha: {
    labelText: "PVsyst Alpha",
    helpText:
      "Absorption coefficient of solar irradiance (this should be matched to the choice of Uc and Uv values)",
    hideIf: ({ cell_temperature_model }) => cell_temperature_model !== "pvsyst",
    disabledIf: ({ pvsyst_mounting_type }) => Boolean(pvsyst_mounting_type),
  },
  voct_model: {
    labelText: "VocT Model",
    options: [
      {
        label: "DeSoto",
        value: "desoto",
        helpText: (
          <>
            W. De Soto, S.A. Klein, W.A. Beckman, ‘Improvement and validation of
            a model for photovoltaic array performance,’ Solar Energy, 80(1),
            78-88, 2006; (
            <Link
              href={"https://doi.org/10.1016/j.solener.2005.04.001"}
              target="_blank"
            >
              https://doi.org/10.1016/j.solener.2005.04.001
            </Link>
            )
          </>
        ),
      },
      {
        label: "Temperature Coefficient Voc",
        value: "tcoefVoc",
        helpText:
          "Apply the temperature-coefficient of open-circuit voltage to determine the temperature-dependent saturation current.",
      },
    ],
  },
  desoto_panel_technology: {
    labelText: "Desoto panel technology",
    hideIf: ({ voct_model }) => voct_model !== "desoto",
  },
  bandgap_temperature_coefficient: {
    labelText: "Bandgap Temperature Coefficient",
    unit: "1/°C",
    hideIf: ({ voct_model }) => voct_model !== "desoto",
    disabledIf: ({ desoto_panel_technology }) =>
      Boolean(desoto_panel_technology),
  },
  cell_material_bandgap: {
    labelText: "Cell Material Bandgap",
    unit: "eV",
    hideIf: ({ voct_model }) => voct_model !== "desoto",
    disabledIf: ({ desoto_panel_technology }) =>
      Boolean(desoto_panel_technology),
  },
};

export const temperatureModelParamsSchema = z.object({
  cell_temperature_model: z.nativeEnum(CellTemperatureModel),
  sandia_panel_type: z.string().nullish(),
  sandia_a: z.number().min(-100).max(0),
  sandia_b: z.number().min(-1).max(0),
  sandia_delta_t: z.number().min(0).max(100),
  sandia_e0: z.number().min(100).max(2000),
  faiman_u0: z.number().min(0).max(1000),
  faiman_u1: z.number().min(0).max(1000),
  pvsyst_mounting_type: z.string().nullish(),
  pvsyst_uc: z.number().min(0).max(1000),
  pvsyst_uv: z.number().min(0).max(1000),
  pvsyst_alpha: z.number().min(0).max(1),
  voct_model: z.string(),
  desoto_panel_technology: z.string().nullish(),
  bandgap_temperature_coefficient: z.number().min(-1).max(1),
  cell_material_bandgap: z.number().min(0).max(10),
});

export const temperatureModelFields = keys(temperatureModelParamsSchema.shape);

export const cellTemperatureModelsRequringWindspeed: CellTemperatureModel[] = [
  CellTemperatureModel.Sandia,
  CellTemperatureModel.Faiman,
  CellTemperatureModel.Pvsyst,
];

export const systemParametersSchema = z
  .object({
    hv_stage_enabled: z.boolean(),
    pv_hv_ac_wire_id: z.string(),
    pv_hv_transformer_id: z.string(),
    grid_injection_limit_enabled: z.boolean(),
    grid_injection_limit: z.number().gt(0),
  })
  .merge(temperatureModelParamsSchema);
